import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export type PetProfileTab = "overview" | "history" | "upcoming" | "packages" | "financial";

interface Tab {
  value: PetProfileTab;
  label: string;
}

interface MobilePetProfileShellProps {
  petName: string;
  subtitleLine: string;
  userInitials: string;
  activeTab: PetProfileTab;
  tabs: Tab[];
  onBack: () => void;
  onTabChange: (tab: PetProfileTab) => void;
  backLabel: string;
  actionsButton: React.ReactNode;
  children: React.ReactNode;
}

export function MobilePetProfileShell({
  petName,
  subtitleLine,
  userInitials,
  activeTab,
  tabs,
  onBack,
  onTabChange,
  backLabel,
  actionsButton,
  children,
}: MobilePetProfileShellProps) {
  return (
    <div className="flex flex-col h-full bg-background-app font-sans">
      {/* Sticky header */}
      <header className="flex-none sticky top-0 z-10 bg-background-app/92 backdrop-blur-md border-b border-border/40 px-4 pt-[max(env(safe-area-inset-top),1rem)] pb-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label={backLabel}
            className="flex-none w-[38px] h-[38px] rounded-full border border-border/60 bg-card flex items-center justify-center shadow-sm"
          >
            <ArrowLeft className="h-[18px] w-[18px] text-foreground" />
          </button>

          <div className="flex-1 min-w-0">
            <p
              className="font-bold text-[22px] leading-[1.1] tracking-tight text-foreground truncate"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {petName}
            </p>
            <p className="text-[12px] text-muted-foreground leading-[1.3] mt-0.5 truncate">
              {subtitleLine}
            </p>
          </div>
        </div>

        {/* Tab pills — horizontal scroll, no scrollbar */}
        <div className="flex gap-2 mt-3.5 pb-3 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {tabs.map((tab) => {
            const isActive = tab.value === activeTab;
            return (
              <button
                key={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={cn(
                  "flex-none px-4 py-[9px] rounded-full text-[14px] font-semibold whitespace-nowrap border transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-[0_6px_14px_rgba(239,100,63,0.22)]"
                    : "bg-card text-foreground border-border/60 shadow-sm"
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden px-4 pt-4 pb-[calc(6.75rem+env(safe-area-inset-bottom))]">
        {children}
      </main>

      {actionsButton}
    </div>
  );
}
